import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import teamService from '../../services/teamService';
import projectService from '../../services/projectService';
import MemberFormModal, { ROLE_GROUPS, getRoleInfo, ACCESS_LEVELS } from './MemberFormModal';
import './Team.css';

/**
 * Map an API user onto the shape this page renders.
 */
const mapMember = (user) => {
  const firstName = user.firstName || '';
  const lastName = user.lastName || '';
  const permissions = user.permissions || {};

  return {
    id: user.id,
    firstName,
    lastName,
    name: `${firstName} ${lastName}`.trim() || user.email,
    email: user.email,
    phone: user.phone || '',
    role: user.role || '',
    customRoleTitle: user.customRoleTitle || '',
    accessLevel: user.accessLevel || 'viewer',
    department: user.department || 'internal',
    certifications: Array.isArray(user.certifications) ? user.certifications : [],
    permissions,
    projectIds: Array.isArray(permissions.projects) ? permissions.projects : [],
    isActive: user.isActive !== false,
    // A member who has never signed in still has an outstanding setup link
    pending: !user.lastLoginAt,
    lastLoginAt: user.lastLoginAt || null,
    createdAt: user.createdAt || null,
  };
};

const getInitials = (name) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

const getAccessLevelLabel = (value) =>
  (ACCESS_LEVELS.find((level) => level.value === value) || { label: value }).label;

const Team = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [forbidden, setForbidden] = useState(false);

  const [projectOptions, setProjectOptions] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // member id being acted on
  const [confirmTarget, setConfirmTarget] = useState(null); // member queued for deactivation

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setForbidden(false);
      const data = await teamService.list();
      setMembers((data.users || []).map(mapMember));
    } catch (err) {
      console.error('Failed to fetch team members:', err);
      if (err.response?.status === 403) {
        // Not a bug to retry - this account simply isn't an organisation admin.
        setForbidden(true);
      } else {
        const message = err.response?.data?.error || 'Failed to load team members';
        setError(message);
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      setProjectsLoading(true);
      const data = await projectService.getAll();
      setProjectOptions(
        (data.projects || []).map((project) => ({ value: project.id, label: project.name }))
      );
    } catch (err) {
      // Not fatal - the modal simply offers no projects to assign
      console.error('Failed to fetch projects for assignment:', err);
      setProjectOptions([]);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
    fetchProjects();
  }, [fetchMembers, fetchProjects]);

  const handleCreateMember = async (memberData) => {
    try {
      setSaving(true);
      const result = await teamService.create(memberData);
      toast.success(
        result?.invitationSent
          ? `Member added. Setup link sent to ${memberData.email}.`
          : 'Member added successfully!'
      );
      setShowAddModal(false);
      fetchMembers();
    } catch (err) {
      console.error('Failed to create team member:', err);
      toast.error(err.response?.data?.error || 'Failed to add team member');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMember = async (memberData) => {
    try {
      setSaving(true);
      await teamService.update(editingMember.id, {
        firstName: memberData.firstName,
        lastName: memberData.lastName,
        email: memberData.email,
        role: memberData.role,
        customRoleTitle: memberData.role === 'other' ? memberData.customRoleTitle : null,
        accessLevel: memberData.accessLevel,
        department: memberData.department,
        phone: memberData.phone || null,
        certifications: memberData.certifications,
        // Keep any permission keys we do not manage from this screen
        permissions: { ...editingMember.permissions, projects: memberData.permissions.projects },
      });
      toast.success('Member updated successfully!');
      setShowEditModal(false);
      setEditingMember(null);
      fetchMembers();
    } catch (err) {
      console.error('Failed to update team member:', err);
      toast.error(err.response?.data?.error || 'Failed to update team member');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (member) => {
    try {
      setPendingAction(member.id);
      await teamService.deactivate(member.id);
      toast.success(`${member.name} has been deactivated`);
      setConfirmTarget(null);
      fetchMembers();
    } catch (err) {
      console.error('Failed to deactivate team member:', err);
      toast.error(err.response?.data?.error || 'Failed to deactivate team member');
    } finally {
      setPendingAction(null);
    }
  };

  const handleReactivate = async (member) => {
    try {
      setPendingAction(member.id);
      await teamService.reactivate(member.id);
      toast.success(`${member.name} has been reactivated`);
      fetchMembers();
    } catch (err) {
      console.error('Failed to reactivate team member:', err);
      toast.error(err.response?.data?.error || 'Failed to reactivate team member');
    } finally {
      setPendingAction(null);
    }
  };

  const handleResendInvite = async (member) => {
    try {
      setPendingAction(member.id);
      await teamService.resendInvite(member.id);
      toast.success(`Setup link resent to ${member.email}`);
    } catch (err) {
      console.error('Failed to resend invite:', err);
      toast.error(err.response?.data?.error || 'Failed to resend setup link');
    } finally {
      setPendingAction(null);
    }
  };

  const openEditModal = (member) => {
    setEditingMember(member);
    setShowEditModal(true);
  };

  // Statistics derived from live data
  const stats = {
    total: members.length,
    active: members.filter((m) => m.isActive).length,
    pending: members.filter((m) => m.isActive && m.pending).length,
    internal: members.filter((m) => m.department === 'internal').length,
    external: members.filter((m) => m.department === 'external').length,
  };

  // Only offer role filters for roles that actually exist in the data
  const presentRoles = ROLE_GROUPS.map((group) => ({
    label: group.label,
    options: group.options.filter((role) => members.some((m) => m.role === role.value)),
  })).filter((group) => group.options.length > 0);

  const filteredMembers = members.filter((member) => {
    const query = searchQuery.toLowerCase();
    const roleInfo = getRoleInfo(member.role, member.customRoleTitle);
    const matchesSearch =
      member.name.toLowerCase().includes(query) ||
      member.email.toLowerCase().includes(query) ||
      roleInfo.label.toLowerCase().includes(query);
    const matchesRole = filterRole === 'all' || member.role === filterRole;
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && member.isActive) ||
      (filterStatus === 'inactive' && !member.isActive) ||
      (filterStatus === 'pending' && member.isActive && member.pending);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getStatusBadge = (member) => {
    if (!member.isActive) {
      return (
        <span className="status-badge inactive">
          <i className="fas fa-circle"></i> Inactive
        </span>
      );
    }
    if (member.pending) {
      return (
        <span className="status-badge pending">
          <i className="fas fa-circle"></i> Pending
        </span>
      );
    }
    return (
      <span className="status-badge active">
        <i className="fas fa-circle"></i> Active
      </span>
    );
  };

  const renderMemberActions = (member) => (
    <>
      <button
        type="button"
        className="action-btn-small"
        title="Edit member"
        onClick={() => openEditModal(member)}
      >
        <i className="fas fa-edit"></i>
      </button>

      {member.isActive && member.pending && (
        <button
          type="button"
          className="action-btn-small"
          title="Resend setup link"
          disabled={pendingAction === member.id}
          onClick={() => handleResendInvite(member)}
        >
          <i className="fas fa-paper-plane"></i>
        </button>
      )}

      {member.isActive ? (
        <button
          type="button"
          className="action-btn-small danger"
          title="Deactivate member"
          disabled={pendingAction === member.id}
          onClick={() => setConfirmTarget(member)}
        >
          <i className="fas fa-user-slash"></i>
        </button>
      ) : (
        <button
          type="button"
          className="action-btn-small"
          title="Reactivate member"
          disabled={pendingAction === member.id}
          onClick={() => handleReactivate(member)}
        >
          <i className="fas fa-user-check"></i>
        </button>
      )}
    </>
  );

  if (loading) {
    return (
      <div className="team-page">
        <div className="team-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="team-page">
      {/* Header */}
      <div className="team-header">
        <div className="header-content">
          <div className="header-title">
            <h1>
              <i className="fas fa-users"></i>
              Team
            </h1>
            <p className="header-subtitle">Manage team members, roles, and project assignments</p>
          </div>
          {!forbidden && (
            <button className="btn-primary-green" onClick={() => setShowAddModal(true)}>
              <i className="fas fa-user-plus"></i>
              Add Member
            </button>
          )}
        </div>
      </div>

      {forbidden ? (
        <div className="team-notice">
          <i className="fas fa-lock"></i>
          <h3>You don&rsquo;t have access to Team management</h3>
          <p>Only organisation admins can view and manage team members. Ask an admin on your team for access.</p>
        </div>
      ) : error ? (
        <div className="team-error">
          <i className="fas fa-exclamation-triangle"></i>
          <h3>We couldn&rsquo;t load your team</h3>
          <p>{error}</p>
          <button type="button" className="btn-text" onClick={fetchMembers}>
            <i className="fas fa-redo"></i> Try again
          </button>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card primary">
              <div className="stat-icon">
                <i className="fas fa-users"></i>
              </div>
              <div className="stat-details">
                <div className="stat-label">Total Members</div>
                <div className="stat-value">{stats.total}</div>
                <div className="stat-trend">{stats.active} active</div>
              </div>
            </div>

            <div className="stat-card success">
              <div className="stat-icon">
                <i className="fas fa-user-check"></i>
              </div>
              <div className="stat-details">
                <div className="stat-label">Active Members</div>
                <div className="stat-value">{stats.active}</div>
                <div className="stat-trend">
                  {stats.pending > 0 ? `${stats.pending} awaiting setup` : 'All signed in'}
                </div>
              </div>
            </div>

            <div className="stat-card info">
              <div className="stat-icon">
                <i className="fas fa-building"></i>
              </div>
              <div className="stat-details">
                <div className="stat-label">Internal Staff</div>
                <div className="stat-value">{stats.internal}</div>
                <div className="stat-trend">Organization members</div>
              </div>
            </div>

            <div className="stat-card warning">
              <div className="stat-icon">
                <i className="fas fa-handshake"></i>
              </div>
              <div className="stat-details">
                <div className="stat-label">External Stakeholders</div>
                <div className="stat-value">{stats.external}</div>
                <div className="stat-trend">Partners &amp; Authorities</div>
              </div>
            </div>
          </div>

          {members.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-user-friends"></i>
              <h3>No team members yet</h3>
              <p>Add your first colleague or stakeholder to start collaborating.</p>
              <button
                type="button"
                className="btn-primary-green"
                onClick={() => setShowAddModal(true)}
              >
                <i className="fas fa-user-plus"></i> Add Member
              </button>
            </div>
          ) : (
            <>
              {/* Controls */}
              <div className="team-controls">
                <div className="search-box">
                  <i className="fas fa-search"></i>
                  <input
                    type="text"
                    placeholder="Search team members..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <div className="filter-row">
                  <div className="filter-group">
                    <label htmlFor="team-filter-role">Role:</label>
                    <select
                      id="team-filter-role"
                      className="filter-select"
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                    >
                      <option value="all">All Roles</option>
                      {presentRoles.map((group) => (
                        <optgroup key={group.label} label={group.label}>
                          {group.options.map((role) => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label htmlFor="team-filter-status">Status:</label>
                    <select
                      id="team-filter-status"
                      className="filter-select"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="pending">Pending setup</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="view-toggle">
                    <button
                      className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                      onClick={() => setViewMode('grid')}
                      title="Grid View"
                    >
                      <i className="fas fa-th"></i>
                    </button>
                    <button
                      className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                      onClick={() => setViewMode('list')}
                      title="List View"
                    >
                      <i className="fas fa-list"></i>
                    </button>
                  </div>
                </div>
              </div>

              {filteredMembers.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-search"></i>
                  <h3>No members found</h3>
                  <p>Try adjusting your search or filters</p>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="team-grid">
                  {filteredMembers.map((member) => {
                    const roleInfo = getRoleInfo(member.role, member.customRoleTitle);
                    return (
                      <div key={member.id} className="member-card">
                        <div className="member-header">
                          <div className="member-avatar" style={{ backgroundColor: roleInfo.color }}>
                            <span>{getInitials(member.name)}</span>
                          </div>
                          {getStatusBadge(member)}
                        </div>

                        <div className="member-info">
                          <h3>{member.name}</h3>
                          <div className="member-role" style={{ color: roleInfo.color }}>
                            <i className={`fas ${roleInfo.icon}`}></i>
                            {roleInfo.label}
                          </div>
                          <span className="access-badge">
                            <i className="fas fa-key"></i>
                            {getAccessLevelLabel(member.accessLevel)} access
                          </span>
                        </div>

                        <div className="member-contact">
                          <div className="contact-item">
                            <i className="fas fa-envelope"></i>
                            <span>{member.email}</span>
                          </div>
                          {member.phone && (
                            <div className="contact-item">
                              <i className="fas fa-phone"></i>
                              <span>{member.phone}</span>
                            </div>
                          )}
                        </div>

                        <div className="member-projects">
                          <div className="projects-label">
                            <i className="fas fa-project-diagram"></i>
                            {member.projectIds.length}{' '}
                            {member.projectIds.length === 1 ? 'Project' : 'Projects'}
                          </div>
                        </div>

                        {member.certifications.length > 0 && (
                          <div className="member-certifications">
                            <i className="fas fa-certificate"></i>
                            {member.certifications.join(', ')}
                          </div>
                        )}

                        <div className="member-actions">{renderMemberActions(member)}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="team-list">
                  <table className="team-table">
                    <thead>
                      <tr>
                        <th>Member</th>
                        <th>Role</th>
                        <th>Access</th>
                        <th>Department</th>
                        <th>Phone</th>
                        <th>Projects</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMembers.map((member) => {
                        const roleInfo = getRoleInfo(member.role, member.customRoleTitle);
                        return (
                          <tr key={member.id}>
                            <td>
                              <div className="member-cell">
                                <div
                                  className="member-avatar-small"
                                  style={{ backgroundColor: roleInfo.color }}
                                >
                                  <span>{getInitials(member.name)}</span>
                                </div>
                                <div className="member-details">
                                  <strong>{member.name}</strong>
                                  <small>{member.email}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="role-badge" style={{ color: roleInfo.color }}>
                                <i className={`fas ${roleInfo.icon}`}></i>
                                {roleInfo.label}
                              </div>
                            </td>
                            <td>
                              <span className="access-badge">
                                <i className="fas fa-key"></i>
                                {getAccessLevelLabel(member.accessLevel)}
                              </span>
                            </td>
                            <td>
                              <span className={`dept-badge ${member.department}`}>
                                {member.department === 'internal' ? 'Internal' : 'External'}
                              </span>
                            </td>
                            <td>
                              <div className="contact-cell">
                                <div>
                                  <i className="fas fa-phone"></i> {member.phone || '\u2014'}
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="projects-count">{member.projectIds.length}</span>
                            </td>
                            <td>{getStatusBadge(member)}</td>
                            <td>
                              <div className="table-actions">{renderMemberActions(member)}</div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Add Member Modal */}
      {showAddModal && (
        <MemberFormModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSave={handleCreateMember}
          saving={saving}
          projectOptions={projectOptions}
          projectsLoading={projectsLoading}
        />
      )}

      {/* Edit Member Modal */}
      {showEditModal && editingMember && (
        <MemberFormModal
          key={editingMember.id}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingMember(null);
          }}
          onSave={handleUpdateMember}
          saving={saving}
          editMode
          projectOptions={projectOptions}
          projectsLoading={projectsLoading}
          initialData={{
            firstName: editingMember.firstName,
            lastName: editingMember.lastName,
            email: editingMember.email,
            phone: editingMember.phone,
            role: editingMember.role,
            customRoleTitle: editingMember.customRoleTitle,
            accessLevel: editingMember.accessLevel,
            department: editingMember.department,
            projects: editingMember.projectIds,
            certifications: editingMember.certifications,
          }}
        />
      )}

      {/* Deactivate confirmation */}
      {confirmTarget && (
        <div className="team-confirm-overlay" onClick={() => setConfirmTarget(null)}>
          <div className="team-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <i className="fas fa-user-slash"></i>
            </div>
            <h3>Deactivate {confirmTarget.name}?</h3>
            <p>
              They will lose access immediately, but their inspections, incidents and reports are
              kept. You can reactivate them at any time.
            </p>
            <div className="confirm-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setConfirmTarget(null)}
                disabled={pendingAction === confirmTarget.id}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={() => handleDeactivate(confirmTarget)}
                disabled={pendingAction === confirmTarget.id}
              >
                {pendingAction === confirmTarget.id ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Deactivating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-user-slash"></i> Deactivate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;
